import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  LocationOn,
  Person,
  Store,
  ShoppingBag,
  Close,
  CalendarToday,
} from '@mui/icons-material';
import { useSelector } from 'react-redux';

const VisitRequestDialog = ({ 
  open, 
  onClose, 
  product, 
  onSubmit, 
  loading = false,
  error = null 
}) => {
  const [formData, setFormData] = useState({
    location: product?.lotLocation || '',
    notes: '',
    preferredDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0], // Tomorrow's date
    preferredTime: ''
  });
  const [formErrors, setFormErrors] = useState({});

  const { user } = useSelector((state) => state.auth);

  // Update location when product changes
  useEffect(() => {
    setFormData(prev => ({
      ...prev,
      location: product?.lotLocation || ''
    }));
  }, [product?.lotLocation]);

  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors = {};
    
    // Location is auto-filled from product.lotLocation, so no validation needed
    // if (!formData.location.trim()) {
    //   errors.location = 'Location is required';
    // }
    
    if (!formData.preferredDate) {
      errors.preferredDate = 'Preferred date is required';
    } else {
      const selectedDate = new Date(formData.preferredDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      if (selectedDate < today) {
        errors.preferredDate = 'Please select a future date';
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    const visitRequestData = {
      customerId: user?.id,
      sellerId: product?.sellerId,
      productId: product?.id,
      location: formData.location.trim(),
      notes: formData.notes.trim() || null,
      preferredDate: formData.preferredDate,
      preferredTime: formData.preferredTime || null
    };

    onSubmit(visitRequestData);
  };

  const handleClose = () => {
    if (!loading) {
      setFormData({ 
        location: product?.lotLocation || '', 
        notes: '', 
        preferredDate: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
      setFormErrors({});
      onClose();
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
            Request Visit
          </Typography>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ minWidth: 'auto', p: 1 }}
          >
            <Close />
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ pt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Product Information Card */}
        <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
          <CardContent sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Product Details
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <ShoppingBag fontSize="small" color="primary" />
                  <Typography variant="body2" fontWeight={500}>
                    {product?.name || 'Product Name'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Store fontSize="small" color="primary" />
                  <Typography variant="body2">
                    Seller ID: {product?.sellerId || 'N/A'}
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Chip 
                  label={`₹${product?.price || '0'}`} 
                  color="primary" 
                  size="small" 
                  sx={{ fontWeight: 600 }}
                />
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Customer Information */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            Customer Information
          </Typography>
          <Box display="flex" alignItems="center" gap={1} sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Person fontSize="small" color="primary" />
            <Typography variant="body2">
              {user?.firstName} {user?.lastName} ({user?.email})
            </Typography>
          </Box>
        </Box>

        {/* Form Fields */}
        <Box component="form" noValidate>
          <TextField
            fullWidth
            label="Lot Location"
            value={formData.location}
            InputProps={{
              readOnly: true,
              startAdornment: (
                <LocationOn sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
              ),
            }}
            helperText={formData.location ? 'Visit location auto-filled from product lot location' : 'No lot location specified for this product'}
            multiline
            rows={2}
            sx={{ 
              mb: 2,
              '& .MuiInputBase-input': {
                backgroundColor: 'grey.50'
              }
            }}
          />

          <TextField
            fullWidth
            label="Preferred Visit Date"
            type="date"
            value={formData.preferredDate}
            onChange={handleInputChange('preferredDate')}
            error={!!formErrors.preferredDate}
            helperText={formErrors.preferredDate || 'Select your preferred date for the visit'}
            required
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <CalendarToday sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
              ),
            }}
            inputProps={{
              min: new Date().toISOString().split('T')[0] // Prevent selecting past dates
            }}
          />

          <TextField
            fullWidth
            label="Preferred Visit Time"
            type="time"
            value={formData.preferredTime}
            onChange={handleInputChange('preferredTime')}
            helperText="Optional: Select your preferred time for the visit"
            sx={{ mb: 2 }}
            InputProps={{
              startAdornment: (
                <CalendarToday sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
              ),
            }}
          />

          <TextField
            fullWidth
            label="Additional Notes"
            placeholder="Any specific requirements or preferences for the visit"
            value={formData.notes}
            onChange={handleInputChange('notes')}
            multiline
            rows={3}
            sx={{ mb: 2 }}
            helperText="Optional: Mention preferred time, specific requirements, etc."
          />
        </Box>

        {/* Information Box */}
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="body2">
            The seller will be notified of your visit request and will contact you to confirm the details.
          </Typography>
        </Alert>
        <Alert severity="warning" sx={{ mt: 2, fontcolor: 'black', fontweight: 'bold'}}>
          <Typography variant="body2">
          <strong>Please note: </strong> Availability of same lot is not guarantee with this request.
            
          </Typography>
        </Alert>

        {/* Disclaimer for time selection */}
        {/* {formData.preferredTime && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            <Typography variant="body2">
              <strong>Please note: </strong> Availability of the same lot cannot be guaranteed.
            </Typography>
          </Alert>
        )} */}
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          variant="outlined"
          sx={{ mr: 1 }}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleSubmit}
          disabled={loading || !formData.preferredDate}
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : null}
        >
          {loading ? 'Submitting...' : 'Submit Request'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VisitRequestDialog;